import firebase from "firebase/compat/app";
import "firebase/compat/firestore";
import moment from "moment";
import { useEffect, useState, useRef } from "react";
import { FaArrowCircleLeft } from "react-icons/fa";
import { useHistory } from "react-router-dom";
import { useLocation } from "react-router-dom/cjs/react-router-dom.min";
import { Col, Row, Spinner, Table } from "reactstrap";
import logo from "../../assets/imgs/pdf-logo.png";
import GeneralButton from "../../components/button/GeneralButton";
import Header from "../../components/globals/Header";
import PageName from "../../components/react_helmet/PageName";
import {
  formatNumberWithCommas,
  uploadBlobAndSendEmail,
} from "../../helpers/helpers";
import useNewQuotes from "./customHooks/useNewQuotes";
import toast from "react-hot-toast";
import { DOCUMENT_PDF_STYLE, DOCUMENT_PRINT_STYLE } from "../../utils/documentPdf";
import useDocumentPdf from "../../hooks/useDocumentPdf";
import DocumentPdfPreview from "../../components/pdf/DocumentPdfPreview";

const PrintQuotePdf = (props) => {
  const {
    billingData,
    shippingData,
    totalTaxAmount,
    subTotal,
    totalAmount,
  } = useNewQuotes(props);
  const { id, data } = useLocation();
  const [state, setState] = useState(data ?? null);
  const history = useHistory();
  const [loading, setLoading] = useState(false);
  const [pageLoader, setPageLoader] = useState(false);

  const printContentRef = useRef(null);
  const pdfRevision = JSON.stringify([
    state?._id, state?.id, state?.created_at, state?.gsaContract, state?.terms,
    state?.expiryDate, state?.created_by, state?.line_items, state?.notes,
    billingData, shippingData, totalTaxAmount, subTotal, totalAmount,
  ]);
  const pdf = useDocumentPdf(printContentRef, pdfRevision, `Quote#${state?.id || "preview"}.pdf`, Boolean(state) && !pageLoader);

  const sendPdfToEmail = async () => {
    setLoading(true);
    try {
      const pdfBlob = pdf.getBlob();
      await uploadBlobAndSendEmail(
        pdfBlob,
        "quotePDF",
        state?._id,
        "Quote",
        "Quotes",
        setLoading,
        state?.id,
        state?.customer?.billing?.email
      );
    } catch (error) {
      console.error("Error sending quote PDF:", error);
      toast.error("Failed to send PDF. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (id) {
      setPageLoader(true);
      const unsubscribe = firebase
        .firestore()
        .collection("Quotes")
        .doc(id)
        .onSnapshot((doc) => {
          if (doc.exists) {
            const currentOrderData = doc.data();
            setState(currentOrderData);
          } else {
            setState({});
          }
          setPageLoader(false);
        }, (error) => {
          setPageLoader(false);
        });
      return () => unsubscribe();
    }
  }, [id]);

  if (pageLoader) {
    return (
      <div
        className="d-flex justify-content-center align-items-center"
        style={{ height: "80vh" }}
      >
        <Spinner color="primary" style={{ width: "5rem", height: "5rem" }} />
      </div>
    );
  }

  return (
    <>
      <PageName title={" Quote Preview"} />
      <div className="d-flex gap-3 mb-3 px-3 mt-3">
        <FaArrowCircleLeft
          className="primary-color pointer mt-1"
          size={30}
          onClick={() => history.goBack()}
        />
        <Header title={`Quote Preview`} />
      </div>

      <DocumentPdfPreview pdf={pdf} title="Quote" />
      <div className="document-pdf-source" aria-hidden="true">
        <section
          className="bg-white shadow-sm pdf-container document-pdf"
          ref={printContentRef}
          style={{ color: "#333", border: "1px solid #dee2e6", margin: "0 auto" }}
        >
          <style>
            {`
              .invoice-card {
                background-color: #fff;
                border: 1px solid #dee2e6;
                border-radius: 4px;
                overflow: hidden;
              }
              .invoice-card-header {
                background-color: #f0f0f0;
                color: #000;
                font-weight: 700;
                font-size: 12.5px;
                letter-spacing: 0.5px;
                padding: 7px 14px;
                text-transform: uppercase;
                border-bottom: 1px solid #dee2e6;
              }
              .invoice-card-body {
                padding: 12px 16px;
                font-size: 13.5px;
                line-height: 1.5;
              }
              .invoice-card-body p {
                margin-bottom: 3px;
              }
              .info-row {
                display: flex;
                justify-content: space-between;
                align-items: center;
                padding: 5px 0;
                border-bottom: 1px solid #dee2e6;
                font-size: 13.5px;
              }
              .info-row:last-child {
                border-bottom: none;
                padding-bottom: 0;
              }
              .invoice-table th {
                background-color: #f0f0f0 !important;
                color: #000 !important;
                font-size: 12px;
                font-weight: 700;
                text-transform: uppercase;
                padding: 8px 10px !important;
                border-color: #dee2e6 !important;
              }
              .invoice-table td {
                padding: 8px 10px !important;
                font-size: 13px;
                border-color: #dee2e6 !important;
              }
            `}
          </style>
          <style>{DOCUMENT_PDF_STYLE + DOCUMENT_PRINT_STYLE}</style>

          {/* Header */}
          <div data-pdf-keep className="d-flex justify-content-between align-items-center mb-4 border-bottom pb-3 pdf-header">
            <img src={logo} className="pdf-logo" style={{ height: "auto", maxWidth: "240px" }} alt="Tech Micro USA" />
            <div className="text-end">
              <h2 className="mb-0 fw-bold pdf-title" style={{ color: "#1a3b5c", letterSpacing: "1px", textTransform: "uppercase", fontSize: "26px" }}>QUOTE</h2>
              <p className="mb-0 text-muted pdf-meta" style={{ fontSize: "13px" }}>Date: {moment(state?.created_at || Date.now()).format("MMM DD, YYYY")}</p>
              <p className="mb-0 text-muted pdf-meta" style={{ fontSize: "13px" }}>Quote #: {state?.id}</p>
            </div>
          </div>

          {/* Row 1: Company Details & Quote Information */}
          <div data-pdf-keep className="row-2-col mb-3" style={{ display: "flex", flexDirection: "row", width: "100%", gap: "16px" }}>
            <div className="col-half" style={{ flex: "1 1 50%", width: "50%" }}>
              <div className="invoice-card h-100">
                <div className="invoice-card-header">COMPANY DETAILS</div>
                <div className="invoice-card-body">
                  <p className="fw-bold text-dark">Techmicro USA</p>
                  <p>206 Far Hills Drive</p>
                  <p>Bolingbrook, IL 60440</p>
                  <p>Sales No: (630) 526 1800</p>
                  <p className="mb-0">www.techmicrousa.com</p>
                </div>
              </div>
            </div>
            <div className="col-half" style={{ flex: "1 1 50%", width: "50%" }}>
              <div className="invoice-card h-100">
                <div className="invoice-card-header">QUOTE INFORMATION</div>
                <div className="invoice-card-body">
                  <div className="info-row">
                    <span className="fw-bold">Quote Number</span>
                    <span>{state?.id}</span>
                  </div>
                  {state?.gsaContract && (
                    <div className="info-row">
                      <span className="fw-bold">GSA Contract #</span>
                      <span>{state?.gsaContract}</span>
                    </div>
                  )}
                  {state?.terms && (
                    <div className="info-row">
                      <span className="fw-bold">Payment Terms</span>
                      <span>{state?.terms}</span>
                    </div>
                  )}
                  <div className="info-row">
                    <span className="fw-bold">Quote Expiry</span>
                    <span>{state?.expiryDate ? moment(state?.expiryDate).format("MMM DD, YYYY") : "Not Given"}</span>
                  </div>
                  <div className="info-row">
                    <span className="fw-bold">Prepared By</span>
                    <span>{typeof state?.created_by === 'object' ? (state?.created_by?.name || state?.created_by?.username || "N/A") : (state?.created_by || "N/A")}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Row 2: Bill To & Ship To */}
          <div data-pdf-keep className="row-2-col mb-3" style={{ display: "flex", flexDirection: "row", width: "100%", gap: "16px" }}>
            <div className="col-half" style={{ flex: "1 1 50%", width: "50%" }}>
              <div className="invoice-card h-100">
                <div className="invoice-card-header">BILL TO</div>
                <div className="invoice-card-body">
                  <p className="fw-bold text-dark">{billingData?.company || `${billingData?.first_name || ""} ${billingData?.last_name || ""}`}</p>
                  {billingData?.company && <p>{`${billingData?.first_name || ""} ${billingData?.last_name || ""}`}</p>}
                  <p>{billingData?.address_1}</p>
                  {billingData?.address_2 && <p>{billingData?.address_2}</p>}
                  <p>{`${billingData?.city || ""}, ${billingData?.state || ""} ${billingData?.postcode || ""}`}</p>
                  <p>Ph: {billingData?.phone || "Not Available"}</p>
                  {billingData?.email && <p className="mb-0">Email: {billingData?.email}</p>}
                </div>
              </div>
            </div>
            <div className="col-half" style={{ flex: "1 1 50%", width: "50%" }}>
              <div className="invoice-card h-100">
                <div className="invoice-card-header">SHIP TO</div>
                <div className="invoice-card-body">
                  <p className="fw-bold text-dark">{shippingData?.company || `${shippingData?.first_name || ""} ${shippingData?.last_name || ""}`}</p>
                  {shippingData?.company && <p>{`${shippingData?.first_name || ""} ${shippingData?.last_name || ""}`}</p>}
                  <p>{shippingData?.address_1}</p>
                  {shippingData?.address_2 && <p>{shippingData?.address_2}</p>}
                  <p>{`${shippingData?.city || ""}, ${shippingData?.state || ""} ${shippingData?.postcode || ""}`}</p>
                  <p>Ph: {shippingData?.phone || "Not Available"}</p>
                  {shippingData?.email && <p className="mb-0">Email: {shippingData?.email}</p>}
                </div>
              </div>
            </div>
          </div>

          <p className="text-muted fst-italic mb-2" style={{ fontSize: "13px" }}>You can place the order by calling us at (630) 526-1800.</p>

          {/* Line Items Table */}
          <div className="mb-3">
            <Table bordered responsive hover className="mb-0 invoice-table" style={{ tableLayout: "fixed", width: "100%" }}>
              <thead>
                <tr className="text-center">
                  <th style={{ width: "15%" }}>ITEM #</th>
                  <th style={{ width: "13%" }}>PART NO.</th>
                  <th style={{ width: "6%" }}>QTY</th>
                  <th className="text-start" style={{ width: "36%" }}>DESCRIPTION</th>
                  <th style={{ width: "13%", whiteSpace: "nowrap" }}>PRICE</th>
                  <th className="text-end" style={{ width: "17%", whiteSpace: "nowrap" }}>EXT PRICE</th>
                </tr>
              </thead>
              <tbody>
                {state?.line_items?.map((item, index) => (
                  <tr className="text-center align-middle" key={index}>
                    <td style={{ wordBreak: "break-all" }}>{item?.sku || "-"}</td>
                    <td style={{ wordBreak: "break-all" }}>{item?.sku?.slice(4) || item?.sku || "-"}</td>
                    <td>{item?.quantity ?? 0}</td>
                    <td className="text-start" style={{ wordBreak: "break-word", overflowWrap: "break-word", whiteSpace: "normal" }}>
                      {item?.description ? item.description.replace(/<br\s*\/?>/gi, " ").replace(/<[^>]+>/g, "").trim() : (item?.name || "N/A")}
                    </td>
                    <td style={{ whiteSpace: "nowrap" }}>${formatNumberWithCommas((parseFloat(item?.price) || 0).toFixed(2))}</td>
                    <td className="text-end fw-bold" style={{ whiteSpace: "nowrap" }}>${formatNumberWithCommas(((parseFloat(item?.price) || 0) * (parseFloat(item?.quantity) || 0)).toFixed(2))}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>

          {/* Row 4: Notes & Totals */}
          <div className="pdf-summary row-2-col mt-3" style={{ display: "flex", flexDirection: "row", justifyContent: "space-between", alignItems: "flex-start", width: "100%", gap: "16px" }}>
            <div style={{ flex: "1 1 auto", minWidth: 0 }}>
              {state?.notes?.length > 0 && (
                <div className="invoice-card mb-0">
                  <div className="invoice-card-header">NOTES</div>
                  <div className="invoice-card-body">
                    {state?.notes?.map((item, index) => (
                      <div key={index} className="mb-2 pb-2 border-bottom" dangerouslySetInnerHTML={{ __html: item?.message ?? "" }} />
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div className="totals-card" style={{ flex: "0 0 320px", width: "320px", minWidth: "320px", marginLeft: "auto" }}>
              <div className="invoice-card">
                <div className="d-flex justify-content-between totals-row border-bottom" style={{ padding: "6px 12px", fontSize: "13px" }}>
                  <span className="fw-bold">Sub Total:</span>
                  <span>${formatNumberWithCommas((parseFloat(subTotal) || 0).toFixed(2))}</span>
                </div>
                <div className="d-flex justify-content-between totals-row border-bottom" style={{ padding: "6px 12px", fontSize: "13px" }}>
                  <span>Tax:</span>
                  <span>${formatNumberWithCommas((parseFloat(totalTaxAmount?.tax) || 0).toFixed(2))}</span>
                </div>
                <div className="d-flex justify-content-between totals-row border-bottom" style={{ padding: "6px 12px", fontSize: "13px" }}>
                  <span>Shipping:</span>
                  <span>${formatNumberWithCommas((parseFloat(totalTaxAmount?.shippingAmount) || 0).toFixed(2))}</span>
                </div>
                <div className="d-flex justify-content-between totals-row border-bottom" style={{ padding: "6px 12px", fontSize: "13px" }}>
                  <span>Insurance:</span>
                  <span>${formatNumberWithCommas((parseFloat(totalTaxAmount?.insurance) || 0).toFixed(2))}</span>
                </div>
                <div className="d-flex justify-content-between totals-row border-bottom" style={{ padding: "6px 12px", fontSize: "13px" }}>
                  <span>Discount:</span>
                  <span className="text-danger">-${formatNumberWithCommas((parseFloat(totalTaxAmount?.discount) || 0).toFixed(2))}</span>
                </div>
                <div className="d-flex justify-content-between totals-final" style={{ padding: "8px 10px", fontSize: "14px", backgroundColor: "#f0f0f0", color: "#000" }}>
                  <span className="fw-bold text-uppercase">QUOTE TOTAL:</span>
                  <span className="fw-bold">${formatNumberWithCommas((parseFloat(totalAmount) || 0).toFixed(2))}</span>
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>

      <Row>
        <Col md={10} className="d-flex flex-wrap justify-content-end mt-0 gap-2 mx-auto mb-4">
          <GeneralButton
            title={"Download PDF"}
            className={"blue-button mb-3"}
            width={"220px"}
            handleClick={pdf.download}
            disabled={!pdf.ready}
          />
          <GeneralButton
            title={"Send PDF"}
            className={"blue-button"}
            width={"175px"}
            handleClick={sendPdfToEmail}
            loading={loading}
            disabled={loading || !pdf.ready}
          />
        </Col>
      </Row>
    </>
  );
};
export default PrintQuotePdf;
